import { useEffect, useState } from 'react'
import { Activity, RefreshCw, Download, FileText, ChevronDown } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DataTable, type DataTableColumn } from '@/components/admin/DataTable'
import { ExportCsvButton } from '@/components/admin/ExportCsvButton'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

// Admin Panel Redesign — merges the old AdminPanel.tsx "logs" tab (Query Logs)
// and "audit" tab (Compliance Audit Logs) into one section with two
// switchable views. Both now own their state/fetching independently instead
// of sharing `queryLogs`/`activeTab` with the rest of the panel — this fixes
// the real bug where the old Users tab's "View Logs" row action clobbered the
// same `queryLogs` state this tab rendered.
//
// Gap fixes made while porting:
//   1. Query Logs now has real pagination (skip + total from the backend,
//      which now returns a true total count) instead of a single fixed-size
//      fetch with no way to see older entries.
//   2. The Compliance Report CSV export now includes the full per-user and
//      per-endpoint breakdown tables that are rendered on screen, not just
//      the 3 summary numbers.

interface QueryLog {
  id: string
  timestamp: string
  endpoint: string
  query?: string
  user_id: string
  user_email: string
  status: string
  response_time_ms?: number
  results_count?: number
  error_message?: string
  ip_address?: string
}

interface AuditLog {
  id: string
  timestamp: string
  endpoint: string
  method: string
  user_id?: string
  user_email?: string
  user_role?: string
  ip_address: string
  user_agent?: string
  request_body?: any
  response_status: number
  response_time_ms?: number
  response_size?: number
  error_message?: string
  compliance_metadata?: {
    regulatory_category?: string
    contains_pii?: boolean
    data_classification?: string
  }
}

interface AuditFilters {
  user_id: string
  user_email: string
  ip_address: string
  endpoint: string
  start_date: string
  end_date: string
  method: string
  status_code: string
}

interface ComplianceReportSummary {
  total_requests?: number
  unique_users?: number
  unique_ip_addresses?: number
}

interface ComplianceReportUserRow {
  user_id?: string
  email?: string
  count?: number
  requests?: number
}

interface ComplianceReportEndpointRow {
  endpoint?: string
  count?: number
}

interface ComplianceReport {
  summary?: ComplianceReportSummary
  by_user?: ComplianceReportUserRow[]
  by_endpoint?: ComplianceReportEndpointRow[]
}

const QUERY_LOGS_PAGE_SIZE = 50
const AUDIT_PAGE_SIZE = 100

function csvCell(value: string | number | null | undefined): string {
  const str = value === null || value === undefined ? '' : String(value)
  return `"${str.replace(/"/g, '""')}"`
}

// Builds a single CSV that concatenates the summary, per-user breakdown, and
// per-endpoint breakdown as separate sections (each with its own header row),
// so the export actually contains everything rendered on screen.
function buildComplianceReportCsv(report: ComplianceReport): string {
  const lines: string[] = []
  const summary = report.summary || {}

  lines.push(csvCell('Compliance Report Summary'))
  lines.push([csvCell('Metric'), csvCell('Value')].join(','))
  lines.push([csvCell('Total Requests'), csvCell(summary.total_requests ?? 0)].join(','))
  lines.push([csvCell('Unique Users'), csvCell(summary.unique_users ?? 0)].join(','))
  lines.push([csvCell('Unique IP Addresses'), csvCell(summary.unique_ip_addresses ?? 0)].join(','))

  if (report.by_user && report.by_user.length > 0) {
    lines.push('')
    lines.push(csvCell('Requests by User'))
    lines.push([csvCell('User'), csvCell('Requests')].join(','))
    report.by_user.forEach((u) => {
      lines.push([csvCell(u.email || u.user_id || ''), csvCell(u.count ?? u.requests ?? 0)].join(','))
    })
  }

  if (report.by_endpoint && report.by_endpoint.length > 0) {
    lines.push('')
    lines.push(csvCell('Requests by Endpoint'))
    lines.push([csvCell('Endpoint'), csvCell('Requests')].join(','))
    report.by_endpoint.forEach((e) => {
      lines.push([csvCell(e.endpoint || ''), csvCell(e.count ?? 0)].join(','))
    })
  }

  return lines.join('\n')
}

export default function LogsAuditSection() {
  const { toast } = useToast()
  const [view, setView] = useState<'query' | 'audit'>('query')

  // --- Query Logs state (fully independent of Audit Logs / any other section) ---
  const [queryLogs, setQueryLogs] = useState<QueryLog[]>([])
  const [queryTotal, setQueryTotal] = useState(0)
  const [querySkip, setQuerySkip] = useState(0)
  const [queryLoading, setQueryLoading] = useState(false)
  const [queryError, setQueryError] = useState<string | null>(null)
  const [logsSearch, setLogsSearch] = useState('')

  // --- Audit Logs state ---
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [auditTotal, setAuditTotal] = useState(0)
  const [auditPage, setAuditPage] = useState(0)
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditFilters, setAuditFilters] = useState<AuditFilters>({
    user_id: '',
    user_email: '',
    ip_address: '',
    endpoint: '',
    start_date: '',
    end_date: '',
    method: '',
    status_code: '',
  })

  // --- Compliance report ---
  const [showComplianceReport, setShowComplianceReport] = useState(false)
  const [complianceReport, setComplianceReport] = useState<ComplianceReport | null>(null)
  const [complianceLoading, setComplianceLoading] = useState(false)

  const loadQueryLogs = async (skip = 0) => {
    setQueryLoading(true)
    setQueryError(null)
    try {
      const result = await apiClient.getSystemLogs({ limit: QUERY_LOGS_PAGE_SIZE, skip })
      const newLogs = ((result as any).logs || []) as QueryLog[]
      setQueryLogs(skip === 0 ? newLogs : (prev) => [...prev, ...newLogs])
      setQueryTotal((result as any).total ?? 0)
      setQuerySkip(skip)
    } catch (e: any) {
      console.error('Failed to load logs', e)
      setQueryError(e.message || 'Failed to load logs')
      toast({ title: 'Failed to load logs', description: e.message, variant: 'destructive' })
    } finally {
      setQueryLoading(false)
    }
  }

  const loadAuditLogs = async (page = 0) => {
    setAuditLoading(true)
    try {
      const params: any = {
        limit: AUDIT_PAGE_SIZE,
        skip: page * AUDIT_PAGE_SIZE,
      }

      if (auditFilters.user_id) params.user_id = auditFilters.user_id
      if (auditFilters.user_email) params.user_email = auditFilters.user_email
      if (auditFilters.ip_address) params.ip_address = auditFilters.ip_address
      if (auditFilters.endpoint) params.endpoint = auditFilters.endpoint
      if (auditFilters.start_date) params.start_date = new Date(auditFilters.start_date).toISOString()
      if (auditFilters.end_date) params.end_date = new Date(auditFilters.end_date).toISOString()
      if (auditFilters.method) params.method = auditFilters.method
      if (auditFilters.status_code) params.status_code = parseInt(auditFilters.status_code, 10)

      const result = await apiClient.getAuditLogs(params)
      const newLogs = ((result as any).logs || []) as AuditLog[]
      setAuditLogs(page === 0 ? newLogs : (prev) => [...prev, ...newLogs])
      setAuditTotal((result as any).total || 0)
      setAuditPage(page)
    } catch (e: any) {
      console.error('Failed to load audit logs', e)
      toast({ title: 'Failed to load audit logs', description: e.message, variant: 'destructive' })
    } finally {
      setAuditLoading(false)
    }
  }

  const generateComplianceReport = async () => {
    if (!auditFilters.start_date || !auditFilters.end_date) {
      toast({ title: 'Please select start and end dates', variant: 'destructive' })
      return
    }
    setComplianceLoading(true)
    setComplianceReport(null)
    try {
      const report = await apiClient.getComplianceReport(
        new Date(auditFilters.start_date).toISOString(),
        new Date(auditFilters.end_date).toISOString(),
        auditFilters.user_id || undefined
      )
      setComplianceReport(report as ComplianceReport)
    } catch (e: any) {
      toast({ title: 'Failed to generate report', description: e.message, variant: 'destructive' })
    } finally {
      setComplianceLoading(false)
    }
  }

  const handleExportComplianceCsv = () => {
    if (!complianceReport) return
    const csv = buildComplianceReportCsv(complianceReport)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `compliance_report_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  useEffect(() => {
    loadQueryLogs(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadAuditLogs(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filteredQueryLogs = queryLogs.filter((l) => {
    if (!logsSearch) return true
    const needle = logsSearch.toLowerCase()
    return (
      l.endpoint?.toLowerCase().includes(needle) ||
      l.user_email?.toLowerCase().includes(needle) ||
      l.query?.toLowerCase().includes(needle)
    )
  })

  const queryLogColumns: DataTableColumn<QueryLog>[] = [
    {
      key: 'timestamp',
      header: 'Timestamp',
      render: (l) => <span className="font-mono text-xs">{new Date(l.timestamp).toLocaleString()}</span>,
    },
    {
      key: 'endpoint',
      header: 'Endpoint',
      render: (l) => (
        <span className="block max-w-xs truncate font-mono text-xs" title={l.endpoint}>
          {l.endpoint}
        </span>
      ),
    },
    {
      key: 'user',
      header: 'User',
      render: (l) => <span className="text-sm">{l.user_email || 'Anonymous'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (l) => <Badge variant={l.status === 'success' ? 'default' : 'destructive'}>{l.status}</Badge>,
    },
    {
      key: 'response_time',
      header: 'Time (ms)',
      render: (l) => <span className="text-xs">{l.response_time_ms ?? 'N/A'}</span>,
    },
    {
      key: 'ip',
      header: 'IP Address',
      render: (l) => <span className="font-mono text-xs">{l.ip_address || '—'}</span>,
    },
    {
      key: 'details',
      header: 'Details',
      render: (l) => (
        <div className="max-w-xs">
          {l.query && (
            <p className="truncate text-xs text-muted-foreground" title={l.query}>
              {l.query}
            </p>
          )}
          {l.error_message && (
            <p className="truncate text-xs text-destructive" title={l.error_message}>
              {l.error_message}
            </p>
          )}
        </div>
      ),
    },
  ]

  const auditLogColumns: DataTableColumn<AuditLog>[] = [
    {
      key: 'timestamp',
      header: 'Timestamp',
      render: (l) => <span className="font-mono text-xs">{new Date(l.timestamp).toLocaleString()}</span>,
    },
    {
      key: 'user',
      header: 'User',
      render: (l) => (
        <div>
          <div className="text-sm font-medium">{l.user_email || 'Anonymous'}</div>
          {l.user_role && (
            <Badge variant="outline" className="text-xs">
              {l.user_role}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'ip',
      header: 'IP Address',
      render: (l) => <span className="font-mono text-xs">{l.ip_address}</span>,
    },
    {
      key: 'method',
      header: 'Method',
      render: (l) => <Badge variant={l.method === 'GET' ? 'outline' : 'default'}>{l.method}</Badge>,
    },
    {
      key: 'endpoint',
      header: 'Endpoint',
      render: (l) => (
        <span className="block max-w-xs truncate font-mono text-xs" title={l.endpoint}>
          {l.endpoint}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (l) => (
        <Badge variant={l.response_status >= 500 ? 'destructive' : l.response_status >= 400 ? 'secondary' : 'default'}>
          {l.response_status}
        </Badge>
      ),
    },
    {
      key: 'time',
      header: 'Time (ms)',
      render: (l) => <span className="text-xs">{l.response_time_ms ?? 'N/A'}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      render: (l) =>
        l.compliance_metadata?.regulatory_category ? (
          <Badge variant="outline" className="text-xs">
            {l.compliance_metadata.regulatory_category}
          </Badge>
        ) : null,
    },
  ]

  return (
    <div className="space-y-4">
      <Tabs value={view} onValueChange={(v) => setView(v as 'query' | 'audit')}>
        <TabsList>
          <TabsTrigger value="query">Query Logs</TabsTrigger>
          <TabsTrigger value="audit">Audit Logs</TabsTrigger>
        </TabsList>

        {/* Query Logs */}
        <TabsContent value="query" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>System Logs & Query History</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">Recent API query logs across all users.</p>
              </div>
              <Button variant="outline" onClick={() => loadQueryLogs(0)} disabled={queryLoading}>
                {queryLoading ? <Activity className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              <div className="mb-3">
                <Input
                  placeholder="Search by endpoint, email, or query..."
                  value={logsSearch}
                  onChange={(e) => setLogsSearch(e.target.value)}
                  className="max-w-md"
                />
              </div>

              {queryError && <p className="mb-3 text-sm text-destructive">{queryError}</p>}

              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Showing {filteredQueryLogs.length} of {queryLogs.length} loaded ({queryTotal} total)
                </p>
                {queryLogs.length < queryTotal && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadQueryLogs(querySkip + QUERY_LOGS_PAGE_SIZE)}
                    disabled={queryLoading}
                  >
                    <ChevronDown className="mr-1 h-4 w-4" />
                    Load More
                  </Button>
                )}
              </div>

              <DataTable
                columns={queryLogColumns}
                rows={filteredQueryLogs}
                getRowId={(l) => l.id || `${l.timestamp}-${l.endpoint}-${l.user_id}`}
                isLoading={queryLoading && queryLogs.length === 0}
                emptyMessage="No logs loaded. Click Refresh to load recent query logs."
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Logs */}
        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Compliance Audit Logs</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => loadAuditLogs(0)} disabled={auditLoading}>
                  {auditLoading ? <Activity className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                  Refresh
                </Button>
                <ExportCsvButton
                  rows={auditLogs}
                  filenamePrefix="audit_logs"
                  columns={[
                    { header: 'Timestamp', value: (l: AuditLog) => new Date(l.timestamp).toLocaleString() },
                    { header: 'User', value: (l: AuditLog) => l.user_email || 'Anonymous' },
                    { header: 'IP', value: (l: AuditLog) => l.ip_address },
                    { header: 'Method', value: (l: AuditLog) => l.method },
                    { header: 'Endpoint', value: (l: AuditLog) => l.endpoint },
                    { header: 'Status', value: (l: AuditLog) => l.response_status },
                    { header: 'Response Time', value: (l: AuditLog) => l.response_time_ms ?? '' },
                  ]}
                />
                <Button variant="outline" onClick={() => setShowComplianceReport(true)}>
                  <FileText className="mr-2 h-4 w-4" />
                  Compliance Report
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="mb-6 space-y-3 rounded border border-border p-4">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <div>
                    <Label className="text-xs">User ID</Label>
                    <Input
                      placeholder="Filter by user ID"
                      value={auditFilters.user_id}
                      onChange={(e) => setAuditFilters({ ...auditFilters, user_id: e.target.value })}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">User Email</Label>
                    <Input
                      placeholder="Filter by email"
                      value={auditFilters.user_email}
                      onChange={(e) => setAuditFilters({ ...auditFilters, user_email: e.target.value })}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">IP Address</Label>
                    <Input
                      placeholder="Filter by IP"
                      value={auditFilters.ip_address}
                      onChange={(e) => setAuditFilters({ ...auditFilters, ip_address: e.target.value })}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Endpoint</Label>
                    <Input
                      placeholder="Filter by endpoint"
                      value={auditFilters.endpoint}
                      onChange={(e) => setAuditFilters({ ...auditFilters, endpoint: e.target.value })}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Method</Label>
                    <Select
                      value={auditFilters.method && auditFilters.method !== '' ? auditFilters.method : undefined}
                      onValueChange={(v) => {
                        if (v === 'all') {
                          setAuditFilters({ ...auditFilters, method: '' })
                        } else {
                          setAuditFilters({ ...auditFilters, method: v })
                        }
                      }}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="All methods" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="GET">GET</SelectItem>
                        <SelectItem value="POST">POST</SelectItem>
                        <SelectItem value="PUT">PUT</SelectItem>
                        <SelectItem value="DELETE">DELETE</SelectItem>
                        <SelectItem value="PATCH">PATCH</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Status Code</Label>
                    <Input
                      placeholder="e.g., 200, 404"
                      value={auditFilters.status_code}
                      onChange={(e) => setAuditFilters({ ...auditFilters, status_code: e.target.value })}
                      className="h-8"
                      type="number"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Start Date</Label>
                    <Input
                      type="datetime-local"
                      value={auditFilters.start_date}
                      onChange={(e) => setAuditFilters({ ...auditFilters, start_date: e.target.value })}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">End Date</Label>
                    <Input
                      type="datetime-local"
                      value={auditFilters.end_date}
                      onChange={(e) => setAuditFilters({ ...auditFilters, end_date: e.target.value })}
                      className="h-8"
                    />
                  </div>
                </div>
                <Button onClick={() => loadAuditLogs(0)} className="w-full" disabled={auditLoading}>
                  Apply Filters
                </Button>
              </div>

              {/* Results Summary */}
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing {auditLogs.length} of {auditTotal} audit log entries
                </p>
                {auditLogs.length < auditTotal && (
                  <Button variant="outline" size="sm" onClick={() => loadAuditLogs(auditPage + 1)} disabled={auditLoading}>
                    <ChevronDown className="mr-1 h-4 w-4" />
                    Load More
                  </Button>
                )}
              </div>

              <DataTable
                columns={auditLogColumns}
                rows={auditLogs}
                getRowId={(l) => l.id}
                isLoading={auditLoading && auditLogs.length === 0}
                emptyMessage="No audit logs found. Try adjusting your filters."
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Compliance Report Dialog */}
      <Dialog
        open={showComplianceReport}
        onOpenChange={(o) => {
          setShowComplianceReport(o)
          if (!o) setComplianceReport(null)
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Compliance Report</DialogTitle>
            <p className="mt-1 text-sm text-muted-foreground">Generate a compliance report for regulatory purposes</p>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="datetime-local"
                  value={auditFilters.start_date}
                  onChange={(e) => setAuditFilters({ ...auditFilters, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="datetime-local"
                  value={auditFilters.end_date}
                  onChange={(e) => setAuditFilters({ ...auditFilters, end_date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>User ID (optional)</Label>
              <Input
                placeholder="Leave empty for all users"
                value={auditFilters.user_id}
                onChange={(e) => setAuditFilters({ ...auditFilters, user_id: e.target.value })}
              />
            </div>
            <Button onClick={generateComplianceReport} className="w-full" disabled={complianceLoading}>
              {complianceLoading ? <Activity className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
              Generate Report
            </Button>

            {complianceReport && (
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded border border-border p-3 text-center">
                    <div className="text-2xl font-bold">{complianceReport.summary?.total_requests ?? 0}</div>
                    <div className="mt-1 text-xs text-muted-foreground">Total Requests</div>
                  </div>
                  <div className="rounded border border-border p-3 text-center">
                    <div className="text-2xl font-bold">{complianceReport.summary?.unique_users ?? 0}</div>
                    <div className="mt-1 text-xs text-muted-foreground">Unique Users</div>
                  </div>
                  <div className="rounded border border-border p-3 text-center">
                    <div className="text-2xl font-bold">{complianceReport.summary?.unique_ip_addresses ?? 0}</div>
                    <div className="mt-1 text-xs text-muted-foreground">Unique IPs</div>
                  </div>
                </div>

                {/* Export Button — includes the summary AND both breakdown tables below */}
                <Button variant="outline" className="w-full" onClick={handleExportComplianceCsv}>
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>

                {/* Breakdown Table — by user */}
                {complianceReport.by_user && complianceReport.by_user.length > 0 && (
                  <div>
                    <h4 className="mb-2 text-sm font-semibold">Requests by User</h4>
                    <div className="max-h-64 overflow-x-auto overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 border-b bg-card">
                          <tr>
                            <th className="p-2 text-left">User</th>
                            <th className="p-2 text-right">Requests</th>
                          </tr>
                        </thead>
                        <tbody>
                          {complianceReport.by_user.map((u, i) => (
                            <tr key={i} className="border-b hover:bg-accent">
                              <td className="p-2">{u.email || u.user_id}</td>
                              <td className="p-2 text-right">{u.count ?? u.requests ?? 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Breakdown Table — by endpoint */}
                {complianceReport.by_endpoint && complianceReport.by_endpoint.length > 0 && (
                  <div>
                    <h4 className="mb-2 text-sm font-semibold">Requests by Endpoint</h4>
                    <div className="max-h-48 overflow-x-auto overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 border-b bg-card">
                          <tr>
                            <th className="p-2 text-left">Endpoint</th>
                            <th className="p-2 text-right">Requests</th>
                          </tr>
                        </thead>
                        <tbody>
                          {complianceReport.by_endpoint.map((e, i) => (
                            <tr key={i} className="border-b hover:bg-accent">
                              <td className="p-2 font-mono text-xs">{e.endpoint}</td>
                              <td className="p-2 text-right">{e.count ?? 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
